import { TAB_KEYS } from '@modules/order/components/ViewSelectorSettings/components/TabComponent/Tabs'
import findIndex from 'lodash/findIndex'
import isEmpty from 'lodash/isEmpty'
import isNumber from 'lodash/isNumber'
import mapValues from 'lodash/mapValues'
import omit from 'lodash/omit'
import pickBy from 'lodash/pickBy'
import reject from 'lodash/reject'
import sortBy from 'lodash/sortBy'
import sum from 'lodash/sum'
import values from 'lodash/values'
import { action, computed, observable } from 'mobx'
import { computedFn } from 'mobx-utils'
import { Column, ColumnType } from '../models/column'
import ColumnMeta from '../models/column-meta'
import { SettingState } from '../models/settings'
import filterColumns from '../utils/filtered-columns'
import flattenDeepByIteratee from '../utils/flatten-deep-by-iteratee'
import shiftColumn from '../utils/shift-columns'

export default class HierarchyWiseItemsTableStore {
  @observable columns: Column[] = []
  @observable settings: SettingState = { columns: {}, rows: {} }
  @observable doorSubHeaderTab = TAB_KEYS[0]
  @observable scrollTopPosition = 0

  @computed get filteredColumns() {
    return isEmpty(this.settings?.columns)
      ? this.sortedColumns
      : filterColumns(this.sortedColumns, this.settings)
  }

  @computed get doorKipHeader() {
    return this.doorSubHeaderTab
  }

  @computed get sortedColumns() {
    if (isEmpty(this.settings?.columns)) {
      return this.columns
    }
    const columnsWithPosition = pickBy(this.settings.columns, val =>
      isNumber(val.position)
    )
    if (isEmpty(columnsWithPosition)) {
      return this.columns
    }
    return sortBy(
      Object.keys(columnsWithPosition).map(field => ({
        field,
        position: columnsWithPosition[field].position
      })),
      'position'
    ).reduce(
      (acc, { field, position }) => shiftColumn(acc, field, position),
      [...this.columns]
    )
  }

  @computed get flattenedColumns() {
    return flattenDeepByIteratee(this.filteredColumns, item => {
      return item.type === ColumnType.Grouped ? item.columns : null
    })
  }

  @computed get flattenedVisibleColumns() {
    return reject(this.flattenedColumns, ['type', ColumnType.Grouped])
  }

  @computed.struct get columnMeta(): {
    [key: string]: ColumnMeta
  } {
    const stack = [...this.filteredColumns]
    let offset = 0
    let visibleColIndex = 0
    const columnMetaMap = {}
    while (stack.length !== 0) {
      const column = stack.shift()
      if (column.columns?.length) {
        columnMetaMap[column.field] = {
          offset,
          visibleColIndex,
          type: column.type
        }
        stack.push(...[...column.columns])
      } else {
        columnMetaMap[column.field] = {
          offset,
          visibleColIndex,
          type: column.type,
          width: column.width
        }
        visibleColIndex++
        offset += column.width
      }
    }
    return columnMetaMap
  }

  @computed get stickyColsPosition() {
    return omit(
      this.filteredColumns
        .filter(({ fixed }) => fixed)
        .reduce(
          (acc, { field, width }) => ({
            ...acc,
            [field]: acc['lastWidth'],
            lastWidth: acc['lastWidth'] + width
          }),
          { lastWidth: 0 }
        ),
      ['lastWidth']
    )
  }

  @action
  setDoorKipTab = tab => {
    this.doorSubHeaderTab = tab
  }

  @action
  setColumns = (columns: Column[], t: any) => {
    columns.map(
      column =>
        (column.label = column.key
          ? t(`order.listview.colHeader.${column.key}`, column.label)
          : column.label)
    )
    this.columns = columns || []
  }

  @action
  updateSettings = (settings: SettingState) => {
    this.settings = settings
  }

  @action
  setScrollTopPosition = value => {
    this.scrollTopPosition = value
  }

  columnMetaByVisibleIndex = computedFn((visibleColIndex: number): ColumnMeta => {
    return values(
      pickBy(
        this.columnMeta,
        value =>
          value.type !== ColumnType.Grouped && visibleColIndex === value.visibleColIndex
      )
    )?.[0]
  })

  columnWidthByVisibleIndex = computedFn(
    (visibleColIndex: number) => this.columnMetaByVisibleIndex(visibleColIndex).width
  )

  columnOffsetWidthByVisibleIndex = computedFn((visibleColIndex: number) => {
    return (
      this.columnMetaByVisibleIndex(visibleColIndex)?.offset -
      sum(
        values(
          mapValues(
            pickBy(this.columnMeta, (value, key) =>
              isNumber(this.stickyColsPosition[key])
            ),
            'width'
          )
        )
      )
    )
  })

  columnOffsetWidthByColumnField = computedFn(
    (field: string) => this.columnMeta[field]?.offset
  )

  priorColumnOffsetWidthByColumnField = computedFn((field: string) => {
    const columnIndex = findIndex(this.flattenedVisibleColumns, ['field', field])
    if (columnIndex === -1) {
      return 0
    }
    const priorColumn = this.flattenedVisibleColumns[columnIndex - 1]
    if (!priorColumn) {
      return 0
    }
    return this.columnOffsetWidthByColumnField(priorColumn.field)
  })

  getColumnByVisibleIndex = computedFn((visibleColIndex: number) => {
    return this.flattenedVisibleColumns[visibleColIndex]
  })
}
